import React, { useEffect, useMemo, useRef, useState } from 'react';
import { businessAPI } from '../../services/api';
import { openExternalWithNotice } from '../../utils/externalLinks';
import { LocationMap } from '../../components/maps/LocationMap';
import {
  CONSTITUTION_LABELS, READINESS_LABELS, ROLE_LABELS, STATUS_LABELS,
  TYPE_LABELS, errorMessage,
} from './businessRegistryUi';
import './DetailModal.css';

const value = data => (data === null || data === undefined || data === '' ? '—' : data);
const date = data => data ? new Intl.DateTimeFormat('mr-IN', { dateStyle: 'medium' }).format(new Date(data)) : '—';

export default function BusinessDetailModal({ tenantSlug, business: summary, onClose, onManage }) {
  const [business, setBusiness] = useState(summary);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const closeRef = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    businessAPI.getAdminById(tenantSlug, summary.business_uid || summary.id)
      .then(response => { if (active) setBusiness(response.data); })
      .catch(requestError => { if (active) setError(errorMessage(requestError, 'तपशील लोड करता आला नाही.')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [summary.business_uid, summary.id, tenantSlug]);

  useEffect(() => {
    closeRef.current?.focus();
    const onKeyDown = event => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const websiteUrl = useMemo(() => business.website
    ? (/^https?:\/\//i.test(business.website) ? business.website : `https://${business.website}`)
    : '', [business.website]);
  const activeRoles = (business.party_roles || []).filter(role => role.status === 'active');
  const activeLocations = (business.locations || []).filter(location => location.status === 'active');
  const primaryLocation = activeLocations.find(location => location.is_primary) || activeLocations[0];
  const grants = business.access_grants || [];

  return (
    <div className="modal-overlay" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="modal-content business-detail-dialog" role="dialog" aria-modal="true" aria-labelledby="business-detail-title">
        <div className="modal-header">
          <div><h2 id="business-detail-title">{business.business_name}</h2><small>{business.business_uid}</small></div>
          <button ref={closeRef} type="button" className="modal-close" aria-label="बंद करा" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {loading && <div className="business-dialog-state" aria-live="polite">तपशील लोड होत आहेत…</div>}
          {error && <div className="business-alert error" role="alert">{error}</div>}

          <div className="business-detail-status">
            <span className={`business-verify ${business.verification_status}`}>{STATUS_LABELS[business.verification_status] || business.verification_status}</span>
            <span className={`business-readiness ${business.tax_readiness?.ready ? 'ready' : 'review'}`}>{business.tax_readiness?.ready ? '✓ करासाठी तयार' : '⚠ करासाठी तपासणी आवश्यक'}</span>
            <span className="business-version">आवृत्ती v{business.version}</span>
          </div>

          {!business.tax_readiness?.ready && Boolean(business.tax_readiness?.missing?.length) && (
            <section className="modal-section readiness-panel">
              <h3>कर-पात्रता अपूर्ण / Tax readiness gaps</h3>
              <ul>{business.tax_readiness.missing.map(item => <li key={item}>{READINESS_LABELS[item] || item}</li>)}</ul>
            </section>
          )}

          <section className="modal-section">
            <h3>व्यवसाय व अनुपालन / Business &amp; compliance</h3>
            <dl className="business-detail-grid">
              <div><dt>प्रदर्शित नाव</dt><dd>{value(business.business_name)}</dd></div>
              <div><dt>कायदेशीर नाव</dt><dd>{value(business.legal_name)}</dd></div>
              <div><dt>रचना</dt><dd>{CONSTITUTION_LABELS[business.constitution_type] || value(business.constitution_type)}</dd></div>
              <div><dt>प्रकार</dt><dd>{TYPE_LABELS[business.business_type] || value(business.business_type)}</dd></div>
              <div><dt>नोंदणी प्रकार</dt><dd>{value(business.registration_type)}</dd></div>
              <div><dt>नोंदणी क्रमांक</dt><dd>{value(business.registration_number)}</dd></div>
              <div><dt>GSTIN (खाजगी)</dt><dd>{value(business.gstin)}</dd></div>
              <div><dt>PAN शेवटची 4 (खाजगी)</dt><dd>{value(business.pan_last4)}</dd></div>
              <div><dt>कर्मचारी</dt><dd>{value(business.employees_count)}</dd></div>
              <div><dt>वार्षिक उलाढाल</dt><dd>{value(business.annual_turnover)}</dd></div>
            </dl>
            {business.description && <p className="business-long-value"><strong>वर्णन:</strong> {business.description}</p>}
            {business.products_services && <p className="business-long-value"><strong>उत्पादने / सेवा:</strong> {business.products_services}</p>}
          </section>

          <section className="modal-section">
            <h3>खाजगी संपर्क / Private contact</h3>
            <dl className="business-detail-grid">
              <div><dt>फोन</dt><dd>{value(business.phone)}</dd></div>
              <div><dt>ई-मेल</dt><dd>{value(business.email)}</dd></div>
              <div><dt>संपर्क व्यक्ती</dt><dd>{value(business.contact_person)}</dd></div>
              <div><dt>संपर्क फोन</dt><dd>{value(business.contact_person_phone)}</dd></div>
              <div><dt>पत्ता</dt><dd>{value(business.address)}</dd></div>
              <div><dt>वेबसाइट</dt><dd>{websiteUrl ? <a href={websiteUrl} target="_blank" rel="noopener noreferrer" onClick={event => openExternalWithNotice(event, websiteUrl)}>{business.website}</a> : '—'}</dd></div>
            </dl>
          </section>

          <section className="modal-section">
            <h3>पक्ष व मालकी / Parties &amp; ownership ({activeRoles.length})</h3>
            {activeRoles.length ? <div className="business-record-list">{activeRoles.map(role => (
              <article key={role.id}>
                <div><strong>{role.party?.display_name || role.party?.legal_name || 'Unknown party'}</strong><small>{role.party?.party_uid}{role.party?.profile_kind ? ` · ${role.party.profile_kind}` : ''}{role.party?.kyc_status ? ` · KYC ${role.party.kyc_status}` : ''}</small></div>
                <span>{ROLE_LABELS[role.role_type] || role.role_type}</span>
                <span>{role.ownership_share !== null && role.ownership_share !== undefined ? `${role.ownership_share}%` : 'हिस्सा लागू नाही'}</span>
                <span>{role.is_primary ? 'प्राथमिक' : ''}{role.is_primary && role.is_liable_party ? ' · ' : ''}{role.is_liable_party ? 'कर-दायी' : ''}</span>
              </article>
            ))}</div> : <p>सक्रिय भूमिका जोडलेल्या नाहीत.</p>}
          </section>

          <section className="modal-section">
            <h3>व्यवसाय ठिकाणे / Locations ({activeLocations.length})</h3>
            {activeLocations.length ? <div className="business-record-list">{activeLocations.map(location => (
              <article key={location.location_uid || location.id}>
                <div><strong>{location.branch_name || (location.is_primary ? 'प्राथमिक ठिकाण' : 'व्यवसाय ठिकाण')}</strong><small>{location.location_uid}</small></div>
                <span>{[location.address_line_1, location.locality, location.ward_number].filter(Boolean).join(', ')}</span>
                <span>{location.property_id ? `मालमत्ता #${location.property_id}` : 'स्वतंत्र पत्ता'}</span>
                <span>{location.is_primary ? 'प्राथमिक' : location.occupancy_type}</span>
              </article>
            ))}</div> : <p>सक्रिय ठिकाण जोडलेले नाही.</p>}
            <LocationMap
              latitude={business.latitude ?? primaryLocation?.latitude}
              longitude={business.longitude ?? primaryLocation?.longitude}
              title={business.business_name}
              address={business.address || primaryLocation?.address_line_1}
              compact
            />
          </section>

          <section className="modal-section">
            <h3>पोर्टल प्रवेश / Access grants ({grants.filter(grant => grant.status === 'active').length} active)</h3>
            {grants.length ? <div className="business-record-list">{grants.map(grant => (
              <article key={grant.grant_uid}>
                <div><strong>{grant.user?.full_name || `User #${grant.user?.id}`}</strong><small>{grant.user?.email || grant.user?.phone}</small></div>
                <span>{(grant.scopes || []).join(', ')}</span>
                <span>{grant.status}</span>
                <span>{date(grant.valid_to)}</span>
              </article>
            ))}</div> : <p>प्रवेश मंजुरी नाही.</p>}
          </section>

          <section className="modal-section">
            <h3>नोंद माहिती / Record metadata</h3>
            <dl className="business-detail-grid">
              <div><dt>तयार</dt><dd>{date(business.created_at)}</dd></div>
              <div><dt>अद्ययावत</dt><dd>{date(business.updated_at)}</dd></div>
              <div><dt>पडताळणी</dt><dd>{date(business.verified_at)}</dd></div>
              <div><dt>संग्रहित</dt><dd>{date(business.archived_at)}</dd></div>
            </dl>
          </section>
        </div>

        <div className="modal-footer">
          {onManage && <button type="button" className="btn-primary" onClick={onManage}>व्यवस्थापन उघडा</button>}
          <button type="button" className="btn-secondary" onClick={onClose}>बंद करा</button>
        </div>
      </div>
    </div>
  );
}
